/**
 * "Create Wireline & Slickline Job" on an accepted Wireline & Slickline quote (WL-5,
 * fieldiq-api#1359, POST /quotes/:id/create-wl-job).
 *
 * Well Servicing's Create Well Servicing Job (CreateWsJobAction), with the
 * RUN KINDS a Wireline & Slickline job carries as its scopes (at least one —
 * slickline, logging, perforating, … from GET /wireline-slickline/meta's
 * runKinds), the WIRELINE UNIT that runs it (GET /wireline-slickline/units —
 * the list the job's Units panel adds from; api#1365 puts it on the job as a
 * job-unit row, on the planned start) and the SUPERVISOR (the people list the
 * WL job form uses). Unit and supervisor are optional at the API, but the unit
 * is asked for as the normal path. The well list is the quote customer's wells
 * (GET /wireline-slickline/wells?customerId=). The day is optional and becomes
 * the job's planned start.
 *
 * A press with no well, or no scope, is SAID, not sent: the API's WELL_REQUIRED
 * / SCOPES_REQUIRED sentence, in the quote screen's refusal notice. Every API
 * refusal goes the same way (wlQuoteRefusal), and success hands the job to the
 * screen, which opens it — a repeat (alreadyCreated) opens the job that
 * already exists.
 *
 * Shown by QuoteDetailScreen only on an accepted Wireline & Slickline quote, only to
 * admin and office staff (the door's requireRole), and only once
 * created-records says the quote has no Wireline & Slickline job yet.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useWlMeta, useWlUnits, useWlUsers, useWlWells } from '../../hooks/useWirelineSlickline';
import { wlQuoteRefusal } from '../../lib/wireline-slickline-quotes';
import type { CreateWlJobResponse } from '../../types/quote-pricing';

const SELECT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white max-w-[16rem]';
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function CreateWlJobAction({
  quoteId,
  customerId,
  onCreated,
  onRefused,
}: {
  quoteId: string;
  customerId: string | null | undefined;
  onCreated: (data: CreateWlJobResponse['data']) => void;
  onRefused: (refusal: { code?: string; error: string }) => void;
}) {
  const [wellId, setWellId] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [unitId, setUnitId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [day, setDay] = useState('');

  const create = useMutation({
    mutationFn: () => api.quotes.createWlJob(quoteId, {
      wellId,
      scopes,
      ...(unitId ? { unitId } : {}),
      ...(supervisorId ? { supervisorId } : {}),
      ...(day ? { scheduledDate: day } : {}),
    }),
    onSuccess: (res: { data?: CreateWlJobResponse }) => {
      const d = res.data?.data;
      if (d?.job?.id) onCreated(d);
    },
    onError: (err) => onRefused(wlQuoteRefusal(err, 'Failed to create the Wireline & Slickline job')),
  });

  const press = () => {
    // No customer on the quote: the API's QUOTE_CUSTOMER_REQUIRED is the reason, so ask it.
    if (customerId && !wellId) {
      onRefused(wlQuoteRefusal({ response: { status: 400, data: { code: 'WELL_REQUIRED' } } }, ''));
      return;
    }
    if (customerId && scopes.length === 0) {
      onRefused(wlQuoteRefusal({ response: { status: 400, data: { code: 'SCOPES_REQUIRED' } } }, ''));
      return;
    }
    create.mutate();
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="create-wl-job">
      {customerId && <WellPicker customerId={customerId} wellId={wellId} onWell={setWellId} />}
      {customerId && <ScopePicker scopes={scopes} onScopes={setScopes} />}
      {customerId && <UnitPicker unitId={unitId} onUnit={setUnitId} />}
      {customerId && <SupervisorPicker supervisorId={supervisorId} onSupervisor={setSupervisorId} />}
      <input
        type="date"
        aria-label="Wireline & Slickline job planned start (optional)"
        value={day}
        onChange={(e) => setDay(e.target.value)}
        className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
      />
      <button onClick={press} disabled={create.isPending} className={ACTION_PRIMARY}>
        {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        Create Wireline & Slickline Job
      </button>
    </span>
  );
}

/** The quote customer's wells. Mounted only with a customer. */
function WellPicker({ customerId, wellId, onWell }: { customerId: string; wellId: string; onWell: (id: string) => void }) {
  const wells = useWlWells({ customerId });
  const rows = wells.data?.data ?? [];
  const empty = wells.isLoading ? 'Loading wells…' : wells.isError ? 'Wells could not be loaded' : rows.length ? 'Choose the well…' : 'No wells for this customer';
  return (
    <select aria-label="Wireline & Slickline job well" value={wellId} onChange={(e) => onWell(e.target.value)} className={SELECT}>
      <option value="">{empty}</option>
      {rows.map((w) => {
        const ident = w.uwi || w.apiNumber;
        return (
          <option key={w.id} value={w.id}>
            {w.wellName}{ident ? ` (${ident})` : ''}
          </option>
        );
      })}
    </select>
  );
}

/** The job's run kinds, from the API's catalog (meta.runKinds), as toggles. At least one is required. */
function ScopePicker({ scopes, onScopes }: { scopes: string[]; onScopes: (next: string[]) => void }) {
  const meta = useWlMeta();
  const catalog = meta.data?.data?.runKinds ?? [];
  if (meta.isLoading) return <span className="text-sm text-fieldiq-muted">Loading scopes…</span>;
  if (catalog.length === 0) return <span className="text-sm text-fieldiq-muted">{meta.isError ? 'Run kinds could not be loaded' : 'No run kinds'}</span>;
  return (
    <span role="group" aria-label="Wireline & Slickline job run kinds" className="inline-flex flex-wrap gap-1.5">
      {catalog.map((s) => {
        const on = scopes.includes(s.value);
        return (
          <label
            key={s.value}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs ${on ? 'border-fi-amber bg-fi-amber/15 text-white' : 'border-fieldiq-border text-fieldiq-text'}`}
          >
            <input
              type="checkbox"
              className="accent-amber-500"
              checked={on}
              onChange={() => onScopes(on ? scopes.filter((x) => x !== s.value) : [...scopes, s.value])}
            />
            {s.label}
          </label>
        );
      })}
    </span>
  );
}

/**
 * The wireline unit that runs the job — the tenant's WL-1 units, the same list
 * the job's Units panel adds from. Asked for as the normal path; the API also
 * takes none (the unit is added on the job later).
 */
function UnitPicker({ unitId, onUnit }: { unitId: string; onUnit: (id: string) => void }) {
  const units = useWlUnits();
  const rows = units.data?.data ?? [];
  const empty = units.isLoading ? 'Loading units…' : units.isError ? 'Units could not be loaded' : rows.length ? 'Choose the unit…' : 'No wireline units yet';
  return (
    <select aria-label="Wireline & Slickline job unit" value={unitId} onChange={(e) => onUnit(e.target.value)} className={SELECT}>
      <option value="">{empty}</option>
      {rows.map((u) => (
        <option key={u.id} value={u.id}>
          {u.unitNumber}{u.equipmentName ? ` · ${u.equipmentName}` : ''}
        </option>
      ))}
    </select>
  );
}

/** The supervisor, from the people list the WL job form uses. Optional — a job can be booked before one is named. */
function SupervisorPicker({ supervisorId, onSupervisor }: { supervisorId: string; onSupervisor: (id: string) => void }) {
  const users = useWlUsers();
  const rows = users.data ?? [];
  return (
    <select aria-label="Wireline & Slickline job supervisor" value={supervisorId} onChange={(e) => onSupervisor(e.target.value)} className={SELECT}>
      <option value="">{users.isLoading ? 'Loading people…' : users.isError ? 'People could not be loaded' : 'Choose the supervisor…'}</option>
      {rows.map((u) => (
        <option key={u.id} value={u.id}>
          {u.name}
        </option>
      ))}
    </select>
  );
}
